/**
 * The catalogue for the order round: picture, name, a price per size (typed
 * in Rands), how many have been ordered, a note, and whether the item shows
 * on the order page. A price change applies to orders placed or changed from
 * then on; orders already placed keep their price.
 */
import { useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { centsToRandsInput, errorDetail, randsToCents } from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderRoundDetail,
  type StaffOrderRoundItem,
} from '@/services/api/staffOrderService'

import { ItemPicture } from '@/pages/staff-order/ItemPicture'

import { formatCount } from './helpers'

interface ItemRowProps {
  roundId: string
  item: StaffOrderRoundItem
  onUpdated: (detail: StaffOrderRoundDetail) => void
}

function ItemRow({ roundId, item, onUpdated }: ItemRowProps) {
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(item.sizes.map((s) => [s.size, centsToRandsInput(s.price_cents)])),
  )
  const [note, setNote] = useState(item.note ?? '')
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)

  const parsed = item.sizes.map((s) => ({ size: s.size, cents: randsToCents(prices[s.size] ?? '') }))
  const invalid = parsed.some((p) => p.cents === null || p.cents <= 0)
  const pricesChanged = parsed.some((p, i) => p.cents !== item.sizes[i].price_cents)
  const noteChanged = note.trim() !== (item.note ?? '').trim()
  const dirty = pricesChanged || noteChanged

  const save = async () => {
    if (invalid || !dirty || saving) return
    setSaving(true)
    try {
      const next = await staffOrderService.updateItem(roundId, item.id, {
        ...(pricesChanged
          ? { sizes: parsed.map((p) => ({ size: p.size, price_cents: p.cents as number })) }
          : {}),
        ...(noteChanged ? { note: note.trim() || null } : {}),
      })
      onUpdated(next)
      toast.success(`${item.name} saved.`)
    } catch (err) {
      toast.error(errorDetail(err, 'The item could not be saved. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (isActive: boolean) => {
    setToggling(true)
    try {
      const next = await staffOrderService.updateItem(roundId, item.id, { is_active: isActive })
      onUpdated(next)
      toast.success(isActive ? `${item.name} is on the order page.` : `${item.name} is hidden from the order page.`)
    } catch (err) {
      toast.error(errorDetail(err, 'The item could not be changed. Please try again.'))
    } finally {
      setToggling(false)
    }
  }

  return (
    <TableRow className={item.is_active ? undefined : 'bg-gray-50 text-gray-500'}>
      <TableCell className="w-14">
        <ItemPicture src={item.image_url} alt={item.name} className="h-12 w-12 rounded border border-gray-100" />
      </TableCell>
      <TableCell className="min-w-[12rem]">
        <div className="font-medium text-gray-900">{item.name}</div>
      </TableCell>
      <TableCell className="min-w-[16rem]">
        <div className="flex flex-wrap gap-2">
          {item.sizes.map((s) => {
            const value = prices[s.size] ?? ''
            const bad = (() => {
              const c = randsToCents(value)
              return c === null || c <= 0
            })()
            return (
              <label key={s.size} className="flex items-center gap-1 text-xs">
                <span className="min-w-[2rem] text-right font-medium text-gray-600">{s.size}</span>
                <Input
                  value={value}
                  onChange={(e) => setPrices((p) => ({ ...p, [s.size]: e.target.value }))}
                  inputMode="decimal"
                  aria-label={`Price for ${item.name}, size ${s.size}, in Rands`}
                  aria-invalid={bad ? true : undefined}
                  className={`h-8 w-24 px-2 text-right tabular-nums ${bad ? 'border-red-400' : ''}`}
                />
              </label>
            )
          })}
        </div>
        {invalid && <p className="mt-1 text-xs text-red-600">Enter each price in Rands, e.g. 310.44</p>}
      </TableCell>
      <TableCell className="text-right tabular-nums">{formatCount(item.ordered_quantity)}</TableCell>
      <TableCell className="min-w-[12rem]">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Shown on the item"
          aria-label={`Note for ${item.name}`}
          className="h-8 text-sm"
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={item.is_active}
            onCheckedChange={toggle}
            disabled={toggling}
            aria-label={`Show ${item.name} on the order page`}
          />
          {toggling && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button type="button" size="sm" variant="outline" onClick={save} disabled={!dirty || invalid || saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
      </TableCell>
    </TableRow>
  )
}

/** A row starts again from the server whenever its prices or note change there. */
const rowKey = (item: StaffOrderRoundItem) =>
  `${item.id}|${item.note ?? ''}|${item.sizes.map((s) => `${s.size}:${s.price_cents}`).join(',')}`

interface ItemsTableProps {
  roundId: string
  items: StaffOrderRoundItem[]
  onUpdated: (detail: StaffOrderRoundDetail) => void
}

export function ItemsTable({ roundId, items, onUpdated }: ItemsTableProps) {
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order)
  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-5">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Items</h4>
          <p className="text-xs text-gray-500">
            A new price applies to orders placed or changed from now on; orders already placed keep
            the price they were placed at. Switch an item off to hide it from the order page.
          </p>
        </div>
        {sorted.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">This order round has no items.</p>
        ) : (
          <div className="rounded-md border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">
                    <span className="sr-only">Picture</span>
                  </TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Price per size (Rands)</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>On the page</TableHead>
                  <TableHead>
                    <span className="sr-only">Save</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((item) => (
                  <ItemRow key={rowKey(item)} roundId={roundId} item={item} onUpdated={onUpdated} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
